import { aujourdhui } from '@/shared/referentiel/conformite';
import { releveCommenceDemo, releveCompletDemo } from './depots-releves';
import type {
  Chantier,
  ModeleEtape,
  DepotChantiers,
  DepotModele,
  EtapeChantier,
  FicheChantier,
  ResultatEnvoi,
  ResumeChantier,
  SaisieEtape,
} from '@/shared/donnees/types';
import {
  dansLOrdre,
  dateCloture,
  etapesARouvrir,
  identifiantDocument,
  peutDemarrer,
  peutRefuser,
  peutRetirer,
  peutSoumettre,
  peutValider,
  prochaineReference,
  sansArchives,
  statutApresAffectation,
  texteRefus,
  verifierRefus,
} from '@/shared/referentiel/chantier';
import {
  adresseFichier,
  compteCourant,
  deposerFichier,
  journaliser,
  lireEtat,
  modifierEtat,
  nouvelId,
  retirerFichier,
  type ChantierDemo,
  type EtapeDemo,
  type EtatDemo,
} from './magasin';

function bientot<T>(valeur: T): Promise<T> {
  return new Promise((resolve) =>
    setTimeout(() => {
      resolve(structuredClone(valeur));
    }, 120)
  );
}

function refus(message: string): never {
  throw new Error(message);
}

/** Les mêmes refus que la base : ni soi-même, ni la réception, ni boucle. */
function verifierAttente(
  modele: readonly ModeleEtape[],
  ligne: ModeleEtape,
  attend: string | null
): string | null {
  if (attend === null) return null;
  if (attend === ligne.id) refus('Une étape ne peut pas s’attendre elle-même.');
  const cible = modele.find((m) => m.id === attend) ?? refus('Étape attendue introuvable.');
  if (ligne.finale || cible.finale) {
    refus('La réception du chantier attend déjà toutes les autres étapes.');
  }
  let suivante: string | null | undefined = cible.attend;
  for (let tours = 0; suivante && tours < 100; tours++) {
    if (suivante === ligne.id) {
      refus('Ces étapes s’attendraient l’une l’autre : personne ne pourrait commencer.');
    }
    suivante = modele.find((m) => m.id === suivante)?.attend;
  }
  return attend;
}

/** En accès libre, aucun e-mail ne part : on le dit. */
const ENVOI_SIMULE: ResultatEnvoi = {
  statut: 'non-envoye',
  raison: 'Accès libre : aucun e-mail n’est envoyé.',
};

function habiller(etat: EtatDemo, chantier: ChantierDemo): Chantier {
  const site = etat.sites.find((s) => s.id === chantier.siteId);
  return {
    ...chantier,
    entrepriseNom:
      etat.entreprises.find((e) => e.id === chantier.entrepriseId)?.raisonSociale ?? '—',
    siteNom: site?.nom ?? '—',
    position: site?.position ?? null,
    commune: site?.commune ?? '',
    departement: site?.departement ?? '',
    /* La base la tient par un déclencheur ; ici, elle se lit sur la réception. */
    clotureLe: dateCloture(etapesDu(etat, chantier.id)),
    partenaireId: chantier.partenaireId ?? null,
    partenaireNom:
      etat.partenaires.find((p) => p.id === chantier.partenaireId)?.raisonSociale ?? null,
  };
}

function ficheDe(etat: EtatDemo, id: string): FicheChantier | null {
  const chantier = etat.chantiers.find((c) => c.id === id);
  const site = chantier ? etat.sites.find((s) => s.id === chantier.siteId) : undefined;
  if (!chantier || !site) return null;
  return {
    ...habiller(etat, chantier),
    etapes: etapesDu(etat, id).map((e) => etapeComplete(etat, e)),
    typesDocument: etat.typesDocument,
    site,
    contactSurPlace:
      etat.contacts.find((c) => c.entrepriseId === chantier.entrepriseId && c.surPlace) ?? null,
  };
}

/** Le nom de l'étape attendue, tant qu'elle n'est pas validée (comme la base). */
function attenteDe(etat: EtatDemo, etape: EtapeDemo): string | null {
  if (!etape.attend) return null;
  const attendue = etat.etapes.find((x) => x.id === etape.attend);
  return attendue && attendue.statut !== 'validee' ? attendue.libelle : null;
}

function etapeComplete(etat: EtatDemo, etape: EtapeDemo): EtapeChantier {
  return {
    ...etape,
    enAttenteDe: attenteDe(etat, etape),
    releveComplet: releveCompletDemo(etape.id),
    releveCommence: releveCommenceDemo(etape.id),
    partenaireNom: etat.partenaires.find((p) => p.id === etape.partenaireId)?.raisonSociale ?? null,
    preuves: etat.preuves
      .filter((p) => p.etapeId === etape.id)
      .sort((a, b) => a.ajouteLe.localeCompare(b.ajouteLe)),
  };
}

function etapesDu(etat: EtatDemo, chantierId: string): EtapeDemo[] {
  return dansLOrdre(etat.etapes.filter((e) => e.chantierId === chantierId));
}

/* Une étape, son chantier et ses sœurs, ou un refus lisible. */
function contexte(etat: EtatDemo, etapeId: string) {
  const etape = etat.etapes.find((e) => e.id === etapeId) ?? refus('Étape introuvable.');
  const chantier =
    etat.chantiers.find((c) => c.id === etape.chantierId) ?? refus('Chantier introuvable.');
  return { etape, chantier, soeurs: etapesDu(etat, chantier.id) };
}

/** En accès libre, un partenaire n'agit que sur ses étapes ; le bureau sur toutes. */
function verifierDroit(etape: EtapeDemo, bureauSeulement = false): void {
  const moi = compteCourant();
  if (!moi) refus('Session fermée.');
  if (moi.role === 'bureau') return;
  if (bureauSeulement) refus('Seul le bureau peut faire cela.');
  if (moi.partenaireId === null || moi.partenaireId !== etape.partenaireId) {
    refus('Cette étape n’est pas confiée à votre entreprise.');
  }
}

function seulementLeBureau(): void {
  const moi = compteCourant();
  if (!moi) refus('Session fermée.');
  if (moi.role !== 'bureau') refus('Seul le bureau peut faire cela.');
}

const libelleEtape = (etape: EtapeDemo, chantier: ChantierDemo) =>
  `${chantier.reference} · ${etape.libelle}`;

/** Changer une étape et le noter au journal, comme le ferait la base. */
function changerEtape(
  e: EtatDemo,
  etape: EtapeDemo,
  chantier: ChantierDemo,
  agir: (etape: EtapeDemo) => void
): void {
  const avant = { ...etape };
  agir(etape);
  journaliser(
    e,
    'modification',
    'etapes_chantier',
    etape.id,
    libelleEtape(etape, chantier),
    avant,
    {
      ...etape,
    }
  );
}

function verifierSaisieEtape(etat: EtatDemo, saisie: SaisieEtape): void {
  if (saisie.libelle.trim() === '') refus('Donnez un nom à l’étape.');
  if (saisie.libelle.trim().length > 120) refus('Le nom tient en 120 caractères au plus.');
  const n = saisie.exigences.photosMin;
  if (!Number.isInteger(n) || n < 0 || n > 20) refus('Le nombre de photos va de 0 à 20.');
  if (saisie.exigences.documents.some((d) => !etat.typesDocument.some((t) => t.id === d))) {
    refus('Un des documents exigés n’existe plus.');
  }
}

export const chantiers: DepotChantiers = {
  lister(options) {
    const etat = lireEtat();
    const tous: ResumeChantier[] = etat.chantiers.map((c) => ({
      ...habiller(etat, c),
      etapes: etapesDu(etat, c.id).map(({ exigences: _exigences, ...e }) => ({
        ...e,
        partenaireNom: etat.partenaires.find((p) => p.id === e.partenaireId)?.raisonSociale ?? null,
        nbPreuves: etat.preuves.filter((p) => p.etapeId === e.id).length,
      })),
    }));
    return bientot(sansArchives(tous, options?.avecArchives));
  },

  lire(id) {
    return bientot(ficheDe(lireEtat(), id));
  },

  fiches(ids) {
    const etat = lireEtat();
    return bientot([...new Set(ids)].map((id) => ficheDe(etat, id)).filter((f) => f !== null));
  },

  creer(saisie) {
    const etat = lireEtat();
    const site = etat.sites.find((s) => s.id === saisie.siteId);
    if (site?.entrepriseId !== saisie.entrepriseId)
      refus('Ce site n’appartient pas à cette entreprise.');
    const chantier: ChantierDemo = {
      id: nouvelId('ch'),
      reference: prochaineReference(
        etat.chantiers.map((c) => c.reference),
        new Date().getFullYear()
      ),
      entrepriseId: saisie.entrepriseId,
      siteId: saisie.siteId,
      titre: saisie.titre.trim(),
      batiment: saisie.batiment,
      notes: saisie.notes,
      creeLe: new Date().toISOString(),
    };
    modifierEtat((e) => {
      e.chantiers.push(chantier);
      /* La chaîne du moment, copiée : la changer ensuite ne touche pas ce chantier. */
      const copies = new Map(dansLOrdre(e.modele).map((m) => [m.id, nouvelId('et')]));
      for (const modele of dansLOrdre(e.modele)) {
        e.etapes.push({
          id: copies.get(modele.id) ?? nouvelId('et'),
          attend: modele.attend ? (copies.get(modele.attend) ?? null) : null,
          chantierId: chantier.id,
          ordre: modele.ordre,
          libelle: modele.libelle,
          metier: modele.metier,
          icone: modele.icone,
          finale: modele.finale,
          partenaireId: null,
          debutPrevu: null,
          finPrevue: null,
          statut: 'a-planifier',
          demarreeLe: null,
          soumiseLe: null,
          valideeLe: null,
          valideePar: null,
          motifReouverture: null,
          reserve: null,
          exigences: {
            photosMin: modele.exigences.photosMin,
            documents: [...modele.exigences.documents],
          },
        });
      }
      journaliser(e, 'creation', 'chantiers', chantier.id, chantier.reference);
    });
    return bientot(habiller(lireEtat(), chantier));
  },

  modifier(id, saisie) {
    modifierEtat((e) => {
      const i = e.chantiers.findIndex((c) => c.id === id);
      const avant = e.chantiers[i] ?? refus('Chantier introuvable.');
      const site = e.sites.find((s) => s.id === saisie.siteId);
      if (site?.entrepriseId !== saisie.entrepriseId)
        refus('Ce site n’appartient pas à cette entreprise.');
      const apres = { ...avant, ...saisie, titre: saisie.titre.trim() };
      e.chantiers[i] = apres;
      journaliser(e, 'modification', 'chantiers', id, apres.reference, avant, apres);
    });
    return bientot(undefined);
  },

  supprimer(id) {
    modifierEtat((e) => {
      const avant = e.chantiers.find((c) => c.id === id) ?? refus('Chantier introuvable.');
      if (e.tickets.some((t) => t.chantierId === id)) {
        refus('Ce chantier a des tickets SAV : supprimez-les avant lui.');
      }
      const ids = new Set(e.etapes.filter((x) => x.chantierId === id).map((x) => x.id));
      e.preuves
        .filter((p) => ids.has(p.etapeId))
        .forEach((p) => {
          retirerFichier(p.chemin);
        });
      e.preuves = e.preuves.filter((p) => !ids.has(p.etapeId));
      e.etapes = e.etapes.filter((x) => x.chantierId !== id);
      e.chantiers = e.chantiers.filter((c) => c.id !== id);
      journaliser(e, 'suppression', 'chantiers', id, avant.reference);
    });
    return bientot(undefined);
  },

  affecter(etapeId, affectation) {
    modifierEtat((e) => {
      const { etape, chantier } = contexte(e, etapeId);
      verifierDroit(etape, true);
      if (
        affectation.debutPrevu &&
        affectation.finPrevue &&
        affectation.finPrevue < affectation.debutPrevu
      ) {
        refus('La fin prévue précède le début.');
      }
      if (etape.statut === 'validee' && affectation.partenaireId !== etape.partenaireId) {
        refus('Cette étape est validée : rouvrez-la avant de changer de partenaire.');
      }
      changerEtape(e, etape, chantier, (x) => {
        x.partenaireId = affectation.partenaireId;
        x.debutPrevu = affectation.debutPrevu;
        x.finPrevue = affectation.finPrevue;
        if (!affectation.debutPrevu) {
          x.heureDebut = null;
          x.heureFin = null;
        } else if (affectation.heures) {
          x.heureDebut = affectation.heures.debut;
          x.heureFin = affectation.heures.fin;
        }
        x.statut = statutApresAffectation(x.statut, affectation);
      });
    });
    return bientot(undefined);
  },

  retirerPartenaire(chantierId, partenaireId) {
    modifierEtat((e) => {
      seulementLeBureau();
      const chantier =
        e.chantiers.find((c) => c.id === chantierId) ?? refus('Chantier introuvable.');
      const siennes = etapesDu(e, chantierId).filter((x) => x.partenaireId === partenaireId);
      const commencees = siennes.filter(
        (x) => (x.statut !== 'a-planifier' && x.statut !== 'planifiee') || x.demarreeLe !== null
      );
      if (commencees.length > 0) {
        refus(
          `Déjà commencé : ${commencees.map((x) => x.libelle.toLowerCase()).join(', ')}. Réaffectez étape par étape depuis la fiche du chantier.`
        );
      }
      for (const etape of siennes) {
        changerEtape(e, etape, chantier, (x) => {
          x.partenaireId = null;
          x.debutPrevu = null;
          x.finPrevue = null;
          x.heureDebut = null;
          x.heureFin = null;
          x.statut = 'a-planifier';
        });
      }
      if (chantier.partenaireId === partenaireId) chantier.partenaireId = null;
    });
    return bientot(undefined);
  },

  affecterChantier(chantierId, partenaireId, periode) {
    modifierEtat((e) => {
      seulementLeBureau();
      if (periode && periode.fin < periode.debut) refus('La fin prévue précède le début.');
      const chantier =
        e.chantiers.find((c) => c.id === chantierId) ?? refus('Chantier introuvable.');
      if (partenaireId && !e.partenaires.some((p) => p.id === partenaireId)) {
        refus('Ce partenaire n’existe plus.');
      }
      const ancien = chantier.partenaireId ?? null;
      chantier.partenaireId = partenaireId;
      for (const etape of etapesDu(e, chantierId)) {
        if (etape.statut === 'validee' || etape.statut === 'a-valider') continue;
        const suit =
          etape.partenaireId === null ||
          etape.partenaireId === ancien ||
          etape.partenaireId === partenaireId;
        if (!suit) continue;
        const affectation = {
          partenaireId,
          debutPrevu: periode?.debut ?? etape.debutPrevu,
          finPrevue: periode?.fin ?? etape.finPrevue,
        };
        changerEtape(e, etape, chantier, (x) => {
          x.partenaireId = affectation.partenaireId;
          x.debutPrevu = affectation.debutPrevu;
          x.finPrevue = affectation.finPrevue;
          x.statut = statutApresAffectation(x.statut, affectation);
        });
      }
    });
    return bientot(undefined);
  },

  demarrer(etapeId, fin = null, debut = null, heures = null) {
    modifierEtat((e) => {
      const { etape, chantier } = contexte(e, etapeId);
      verifierDroit(etape);
      const verdict = peutDemarrer(
        { ...etape, enAttenteDe: attenteDe(e, etape) },
        e.etapes.filter((x) => x.chantierId === etape.chantierId)
      );
      if (!verdict.ok) refus(verdict.raison);
      const jour = aujourdhui();
      if (fin !== null && fin < jour) refus('La fin prévue ne peut pas être passée.');
      const depart = debut ?? jour;
      if (fin !== null && fin < depart) refus('La fin ne peut pas précéder le début.');
      changerEtape(e, etape, chantier, (x) => {
        x.statut = 'en-cours';
        x.demarreeLe = new Date().toISOString();
        x.debutPrevu = depart;
        x.finPrevue = fin ?? (x.finPrevue && x.finPrevue > depart ? x.finPrevue : depart);
        if (heures) {
          x.heureDebut = heures.debut;
          x.heureFin = heures.fin;
        }
      });
    });
    return bientot(undefined);
  },

  annoncer(etapeId, debut, fin, heures = null) {
    modifierEtat((e) => {
      const { etape, chantier } = contexte(e, etapeId);
      verifierDroit(etape);
      if (etape.statut === 'a-valider' || etape.statut === 'validee') {
        refus('Cette étape est terminée : ses dates ne bougent plus.');
      }
      const enCours = etape.statut === 'en-cours' || etape.statut === 'rouverte';
      const depart = enCours ? (debut ?? etape.debutPrevu ?? aujourdhui()) : debut;
      if (depart === null) refus('Indiquez la date de début.');
      if (fin < depart) refus('La fin ne peut pas précéder le début.');
      changerEtape(e, etape, chantier, (x) => {
        x.finPrevue = fin;
        if (heures) {
          x.heureDebut = heures.debut;
          x.heureFin = heures.fin;
        }
        if (enCours && debut) x.debutPrevu = debut;
        if (!enCours) {
          x.debutPrevu = depart;
          if (x.partenaireId) x.statut = 'planifiee';
        }
      });
    });
    return bientot(undefined);
  },

  async ajouterPreuve(etapeId, fichier, type) {
    const etat = lireEtat();
    const { etape, chantier } = contexte(etat, etapeId);
    verifierDroit(etape);
    if (etape.statut === 'validee')
      refus('Cette étape est validée : rouvrez-la pour ajouter une preuve.');
    const id = nouvelId('pr');
    const chemin = `chantiers/${chantier.id}/${etape.id}/${id}-${fichier.name}`;
    await deposerFichier(chemin, fichier);
    modifierEtat((e) => {
      e.preuves.push({
        id,
        etapeId,
        type,
        nom: fichier.name,
        taille: fichier.size,
        typeMime: fichier.type || 'application/octet-stream',
        chemin,
        ajouteLe: new Date().toISOString(),
        ajoutePar: compteCourant()?.id ?? null,
      });
      journaliser(e, 'creation', 'preuves', id, fichier.name);
    });
    return bientot(undefined);
  },

  supprimerPreuve(id) {
    modifierEtat((e) => {
      const preuve = e.preuves.find((p) => p.id === id) ?? refus('Preuve introuvable.');
      const { etape } = contexte(e, preuve.etapeId);
      verifierDroit(etape);
      if (etape.statut === 'validee')
        refus('Cette étape est validée : ses preuves ne se retirent plus.');
      retirerFichier(preuve.chemin);
      e.preuves = e.preuves.filter((p) => p.id !== id);
      journaliser(e, 'suppression', 'preuves', id, preuve.nom);
    });
    return bientot(undefined);
  },

  adressePreuve(chemin) {
    const nom = lireEtat().preuves.find((p) => p.chemin === chemin)?.nom ?? chemin;
    return adresseFichier(chemin, nom);
  },

  reprendreEnvoi(etapeId) {
    modifierEtat((e) => {
      const { etape } = contexte(e, etapeId);
      verifierDroit(etape);
      if (etape.statut !== 'a-valider') {
        refus('Seule une étape envoyée au bureau, pas encore validée, peut être reprise.');
      }
      etape.statut = 'en-cours';
    });
    return bientot(undefined);
  },

  soumettre(etapeId) {
    modifierEtat((e) => {
      const { etape, chantier, soeurs } = contexte(e, etapeId);
      verifierDroit(etape);
      const verdict = peutSoumettre(
        {
          ...etape,
          enAttenteDe: attenteDe(e, etape),
          releveComplet: releveCompletDemo(etape.id),
          preuves: e.preuves.filter((p) => p.etapeId === etape.id),
        },
        soeurs,
        e.typesDocument
      );
      if (!verdict.ok) refus(verdict.raison);
      changerEtape(e, etape, chantier, (x) => {
        x.statut = 'a-valider';
        x.soumiseLe = new Date().toISOString();
        x.demarreeLe ??= new Date().toISOString();
      });
    });
    return bientot(undefined);
  },

  valider(etapeId) {
    modifierEtat((e) => {
      const { etape, chantier, soeurs } = contexte(e, etapeId);
      verifierDroit(etape, true);
      const verdict = peutValider(
        {
          ...etape,
          enAttenteDe: attenteDe(e, etape),
          releveComplet: releveCompletDemo(etape.id),
          preuves: e.preuves.filter((p) => p.etapeId === etape.id),
        },
        soeurs,
        e.typesDocument
      );
      if (!verdict.ok) refus(verdict.raison);
      const moi = compteCourant();
      changerEtape(e, etape, chantier, (x) => {
        x.statut = 'validee';
        x.demarreeLe ??= new Date().toISOString();
        x.valideeLe = new Date().toISOString();
        x.valideePar = moi ? moi.nom || moi.email : null;
        x.motifReouverture = null;
      });
    });
    return bientot(undefined);
  },

  refuser(etapeId, motifs) {
    modifierEtat((e) => {
      const { etape, chantier } = contexte(e, etapeId);
      verifierDroit(etape, true);
      const verdict = peutRefuser(etape);
      if (!verdict.ok) refus(verdict.raison);
      const complet = verifierRefus(motifs.motifs, etape.exigences);
      if (!complet.ok) refus(complet.raison);
      changerEtape(e, etape, chantier, (x) => {
        x.statut = 'rouverte';
        x.motifReouverture = texteRefus(motifs, e.typesDocument);
        x.soumiseLe = null;
      });
    });
    return bientot(ENVOI_SIMULE);
  },

  signalerReserve(etapeId, texte) {
    if (texte.trim() === '') return Promise.reject(new Error('Décrivez la réserve.'));
    if (texte.trim().length > 2000) {
      return Promise.reject(new Error('La réserve tient en 2 000 caractères au plus.'));
    }
    modifierEtat((e) => {
      const { etape, chantier } = contexte(e, etapeId);
      verifierDroit(etape);
      const moi = compteCourant();
      changerEtape(e, etape, chantier, (x) => {
        x.reserve = {
          texte: texte.trim(),
          signaleeLe: new Date().toISOString(),
          signaleePar: moi ? moi.nom || moi.email : null,
        };
      });
    });
    return bientot(undefined);
  },

  leverReserve(etapeId) {
    modifierEtat((e) => {
      const { etape, chantier } = contexte(e, etapeId);
      verifierDroit(etape, true);
      if (!etape.reserve) refus('Aucune réserve à lever.');
      changerEtape(e, etape, chantier, (x) => {
        x.reserve = null;
      });
    });
    return bientot(undefined);
  },

  rouvrir(etapeId, motif) {
    if (motif.trim() === '')
      return Promise.reject(new Error('Dites pourquoi l’étape est rouverte.'));
    modifierEtat((e) => {
      const { etape, chantier, soeurs } = contexte(e, etapeId);
      verifierDroit(etape, true);
      const ids = etapesARouvrir(etape, soeurs);
      if (ids.length === 0) refus('Seule une étape validée peut être rouverte.');
      for (const id of ids) {
        const cible = e.etapes.find((x) => x.id === id);
        if (!cible) continue;
        changerEtape(e, cible, chantier, (x) => {
          x.statut = 'rouverte';
          x.valideeLe = null;
          x.valideePar = null;
          x.soumiseLe = null;
          x.motifReouverture =
            id === etape.id
              ? motif.trim()
              : `Rouverte avec « ${etape.libelle.toLowerCase()} » : ${motif.trim()}`;
        });
      }
    });
    return bientot(undefined);
  },

  ajouterEtape(chantierId, saisie) {
    modifierEtat((e) => {
      seulementLeBureau();
      verifierSaisieEtape(e, saisie);
      const chantier =
        e.chantiers.find((c) => c.id === chantierId) ?? refus('Chantier introuvable.');
      const etapes = etapesDu(e, chantierId);
      const finale = etapes.find((x) => x.finale);
      if (finale?.statut === 'validee')
        refus('Le chantier est clôturé : rouvrez d’abord sa réception.');
      const ordre = finale ? finale.ordre : etapes.length + 1;
      if (finale) finale.ordre = ordre + 1;
      const id = nouvelId('et');
      e.etapes.push({
        id,
        chantierId,
        ordre,
        libelle: saisie.libelle.trim(),
        metier: saisie.metier,
        icone: saisie.icone,
        finale: false,
        partenaireId: null,
        debutPrevu: null,
        finPrevue: null,
        statut: 'a-planifier',
        demarreeLe: null,
        soumiseLe: null,
        valideeLe: null,
        valideePar: null,
        motifReouverture: null,
        reserve: null,
        exigences: {
          photosMin: saisie.exigences.photosMin,
          documents: [...saisie.exigences.documents],
        },
      });
      journaliser(
        e,
        'creation',
        'etapes_chantier',
        id,
        `${chantier.reference} · ${saisie.libelle.trim()}`
      );
    });
    return bientot(undefined);
  },

  retirerEtape(etapeId) {
    modifierEtat((e) => {
      seulementLeBureau();
      const { etape, chantier } = contexte(e, etapeId);
      const verdict = peutRetirer({
        ...etape,
        nbPreuves: e.preuves.filter((p) => p.etapeId === etape.id).length,
      });
      if (!verdict.ok) refus(verdict.raison);
      e.etapes = e.etapes.filter((x) => x.id !== etapeId);
      journaliser(e, 'suppression', 'etapes_chantier', etapeId, libelleEtape(etape, chantier));
    });
    return bientot(undefined);
  },

  historique(chantierId) {
    const etat = lireEtat();
    const etapes = new Set(etat.etapes.filter((e) => e.chantierId === chantierId).map((e) => e.id));
    const preuves = new Set(etat.preuves.filter((p) => etapes.has(p.etapeId)).map((p) => p.id));
    return bientot(
      etat.journal
        .filter(
          (j) =>
            (j.objetType === 'chantiers' && j.objetId === chantierId) ||
            (j.objetType === 'etapes_chantier' && etapes.has(j.objetId)) ||
            (j.objetType === 'preuves' && preuves.has(j.objetId))
        )
        .slice(0, 100)
        .map((j) => ({
          id: j.id,
          horodatage: j.horodatage,
          acteurNom: j.acteurNom,
          action: j.action,
          objetType: j.objetType,
          libelle: j.libelle,
          changements: j.changements,
        }))
    );
  },
};

export const modele: DepotModele = {
  lire() {
    return bientot(dansLOrdre(lireEtat().modele));
  },

  ajouter(saisie) {
    modifierEtat((e) => {
      seulementLeBureau();
      verifierSaisieEtape(e, saisie);
      const finale = e.modele.find((m) => m.finale);
      const ordre = finale ? finale.ordre : e.modele.length + 1;
      if (finale) finale.ordre = ordre + 1;
      const id = nouvelId('me');
      e.modele.push({
        id,
        ordre,
        libelle: saisie.libelle.trim(),
        metier: saisie.metier,
        icone: saisie.icone,
        finale: false,
        exigences: {
          photosMin: saisie.exigences.photosMin,
          documents: [...saisie.exigences.documents],
        },
      });
      journaliser(e, 'creation', 'modele_etapes', id, saisie.libelle.trim());
    });
    return bientot(undefined);
  },

  modifier(id, saisie) {
    modifierEtat((e) => {
      seulementLeBureau();
      verifierSaisieEtape(e, saisie);
      const ligne = e.modele.find((m) => m.id === id) ?? refus('Étape introuvable.');
      const avant = { ...ligne, exigences: { ...ligne.exigences } };
      ligne.libelle = saisie.libelle.trim();
      ligne.metier = ligne.finale ? 'reception' : saisie.metier;
      ligne.icone = saisie.icone;
      ligne.exigences = {
        photosMin: saisie.exigences.photosMin,
        documents: [...saisie.exigences.documents],
      };
      if (saisie.attend !== undefined && saisie.attend !== (ligne.attend ?? null)) {
        ligne.attend = verifierAttente(e.modele, ligne, saisie.attend);
        /* Les chantiers en cours suivent ; les clôturés gardent leur histoire. */
        const attendue = e.modele.find((m) => m.id === ligne.attend);
        for (const chantier of e.chantiers) {
          if (dateCloture(etapesDu(e, chantier.id))) continue;
          const soeurs = etapesDu(e, chantier.id);
          const copie = soeurs.find((x) => x.libelle === avant.libelle);
          if (!copie) continue;
          copie.attend = attendue
            ? (soeurs.find((x) => x.libelle === attendue.libelle)?.id ?? null)
            : null;
        }
      }
      journaliser(e, 'modification', 'modele_etapes', id, ligne.libelle, avant, { ...ligne });
    });
    return bientot(undefined);
  },

  supprimer(id) {
    modifierEtat((e) => {
      seulementLeBureau();
      const ligne = e.modele.find((m) => m.id === id) ?? refus('Étape introuvable.');
      if (ligne.finale) refus('La réception du chantier ne se retire pas de la chaîne.');
      e.modele = e.modele.filter((m) => m.id !== id);
      journaliser(e, 'suppression', 'modele_etapes', id, ligne.libelle);
    });
    return bientot(undefined);
  },

  ordonner(ids) {
    modifierEtat((e) => {
      seulementLeBureau();
      ids.forEach((id, n) => {
        const ligne = e.modele.find((m) => m.id === id && !m.finale);
        if (ligne) ligne.ordre = n + 1;
      });
      const finale = e.modele.find((m) => m.finale);
      if (finale) {
        finale.ordre = Math.max(0, ...e.modele.filter((m) => !m.finale).map((m) => m.ordre)) + 1;
      }
    });
    return bientot(undefined);
  },

  documents() {
    return bientot(
      [...lireEtat().typesDocument].sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr'))
    );
  },

  ajouterDocument(libelle) {
    const nom = libelle.trim();
    if (nom === '') return Promise.reject(new Error('Donnez un nom au document.'));
    if (nom.length > 120) {
      return Promise.reject(new Error('Le nom tient en 120 caractères au plus.'));
    }
    let cree = { id: '', libelle: nom };
    modifierEtat((e) => {
      seulementLeBureau();
      if (e.typesDocument.some((t) => t.libelle.toLowerCase() === nom.toLowerCase())) {
        refus('Ce document existe déjà.');
      }
      cree = {
        id: identifiantDocument(
          nom,
          e.typesDocument.map((t) => t.id)
        ),
        libelle: nom,
      };
      e.typesDocument.push(cree);
      journaliser(e, 'creation', 'types_document', cree.id, nom);
    });
    return bientot(cree);
  },

  renommerDocument(id, libelle) {
    const nom = libelle.trim();
    if (nom === '') return Promise.reject(new Error('Donnez un nom au document.'));
    modifierEtat((e) => {
      seulementLeBureau();
      const doc = e.typesDocument.find((t) => t.id === id) ?? refus('Document introuvable.');
      const avant = { ...doc };
      doc.libelle = nom;
      journaliser(e, 'modification', 'types_document', id, nom, avant, { ...doc });
    });
    return bientot(undefined);
  },

  supprimerDocument(id) {
    modifierEtat((e) => {
      seulementLeBureau();
      const doc = e.typesDocument.find((t) => t.id === id) ?? refus('Document introuvable.');
      if (
        e.modele.some((m) => m.exigences.documents.includes(id)) ||
        e.etapes.some((x) => x.statut !== 'validee' && x.exigences.documents.includes(id))
      ) {
        refus(
          'Ce document est exigé par une étape : retirez-le d’abord de la chaîne et des chantiers en cours.'
        );
      }
      e.typesDocument = e.typesDocument.filter((t) => t.id !== id);
      journaliser(e, 'suppression', 'types_document', id, doc.libelle);
    });
    return bientot(undefined);
  },
};
